import { formatCurrency, getLocaleNumberFormatNoDecimals } from '@repo/i18n';
import {
    BilberryPromoCodeStatus,
    CartItem,
    Currency,
    MembershipPaymentPlan,
    OctoBooking,
    OctoOrder,
    PackageTicketOptionWithQuantity,
    ProductInstance,
    TicketOptionWithQuantity,
    TicketType,
    Translations,
    CheckoutInfo,
    NativeCalculateResult,
} from '@repo/types';
import { getPriceFromTicketOption } from '@repo/widget-utils/price-helper';
import {
    getProjectsFromCart,
    getToursFromCart,
    nativeProjectLineKey,
    nativeTourLineKey,
} from '@repo/widget-utils/ProductMapper';
import {
    getCartItemId,
    getCartItemSummaryDateInformation,
    isAccomodationOrNightsCartItem,
    isActivityCartItem,
    isExtraCartItem,
    isPackageCartItem,
    isTimeslotCartItem,
    isWaitingListTicketOption,
} from '@repo/widget-utils/cart/cartUtils';
import { groupBy, sumBy } from 'lodash-es';
import { getCartItemDisplayTitle } from '@repo/widget-utils/display-helper';
import { tzdate } from '@repo/tzdate';
import { mergeAccommodationSummaryLines } from './accommodation-summary';

export type PromoCodeUsageContext = {
    promoCodeUsages: Map<string, number>;
    promoCodeUsagesPerOrder: Map<string, number>;
};

export type SummaryCartItem = {
    uniqueKey: string; // Unique key to identify the cart item, used for editing/removing the item
    priceLines: SummaryPriceLineData[];
    expectedPriceLinesCount: number; // Used to determine how many loading skeleton lines to show while prices are loading
    isLoadingPrices: boolean;
    displayTitle: string; // Pre-formatted title to show in the cart item header
    dateInformation?: string; // Pre-formatted date information to show in the cart item header
    supportsEdit: boolean;
};

export type SummaryPriceLineData = {
    ticketName: string;
    ticketDetail?: string;
    ticketDetailStrikeThrough: boolean;
    priceText: string;
    priceTextStrikeThrough?: boolean;
    priceSecondaryLabel?: string;
    isFree?: boolean;
    depositMessage?: string;
    isLoadingPrice: boolean;
};

function getPriceText(
    isFree: boolean,
    creditCost: number,
    currencyCost: number,
    locale: string,
    t: Translations,
) {
    if (isFree) return t.free;

    if (creditCost > 0) {
        return `${creditCost} ${t.credit.num(creditCost).toLowerCase()}`;
    }

    return getLocaleNumberFormatNoDecimals(locale, currencyCost);
}

export function mapQuantityToDetailedSummaryPriceLineForMembership(
    paymentPlans: MembershipPaymentPlan[],
    isAccommodation: boolean,
    isLoadingPrices: boolean,
    locale: string,
    t: Translations,
    overrideQuantity?: number,
): SummaryPriceLineData[] {
    const byAggregateKey = groupBy(paymentPlans, (x) => {
        const creditCost = sumBy(x.valueCardUsages, (u) => u.creditCost);
        const priceReductionPercentage = sumBy(
            x.valueCardUsages,
            (u) => u.priceReductionPercentage,
        );

        return overrideQuantity
            ? `` // We don't want to differentitate group at all if quantity is overriden, because that will make the aggregation incorrect
            : `${x.ticketName}|${creditCost}|${priceReductionPercentage}|${x.currencyCost?.price}`;
    });

    const aggregatedUsage = Object.entries(byAggregateKey).map(([key, x]) => {
        const [firstPlan] = x;
        const usages = x.flatMap((v) => v.valueCardUsages);
        const [firstUsage] = usages;

        const creditCostSum = sumBy(usages, (x) => x.creditCost);
        const currencyCostSum = sumBy(x, (x) => x.currencyCost?.price ?? 0);

        const isFree = creditCostSum === 0 && currencyCostSum === 0;
        const hasMembershipBenefit = usages.length > 0;

        const allRates = x.flatMap((x) => x.currencyCost?.rates ?? []);
        const originalCostSum = sumBy(allRates, (x) => x.originalPrice);

        return {
            key: key,
            ticketQuantity: overrideQuantity ?? x.length,
            ticketName: firstPlan.ticketName,
            creditCost: creditCostSum / 100,
            currencyCost: currencyCostSum / 100,
            priceReductionPercentage: firstUsage?.priceReductionPercentage ?? 0,
            isFree,
            hasMembershipBenefit,
            originalCost: originalCostSum / 100,
        };
    });

    if (aggregatedUsage.length === 0) {
        return [createSummaryPriceLinePlaceholder(isLoadingPrices)];
    }

    return aggregatedUsage.map((x) => {
        const isDiscounted = x.currencyCost < x.originalCost;

        return {
            key: x.key,
            ticketName: getTicketQuantityText(x.ticketQuantity, x.ticketName),
            priceText: getPriceText(x.isFree, x.creditCost, x.currencyCost, locale, t),
            priceSecondaryLabel:
                x.isFree && x.hasMembershipBenefit ? t.inclInMembership : undefined,
            isFree: x.isFree,
            ticketDetail: isDiscounted
                ? `${getLocaleNumberFormatNoDecimals(locale, x.originalCost)}`
                : undefined,
            ticketDetailStrikeThrough: isDiscounted,
            isLoadingPrice: isLoadingPrices,
        };
    });
}

export function mapQuantityToSummaryPriceLineForMembership(
    paymentPlans: MembershipPaymentPlan[],
    isAccommodation: boolean,
    isLoadingPrices: boolean,
    locale: string,
    t: Translations,
    overrideQuantity?: number,
): SummaryPriceLineData[] {
    if (!paymentPlans.length) {
        return [createSummaryPriceLinePlaceholder(isLoadingPrices)];
    }

    const [firstPlan] = paymentPlans;
    const usages = paymentPlans.flatMap((v) => v.valueCardUsages);

    const creditCostSum = sumBy(usages, (x) => x.creditCost);
    const currencyCostSum = sumBy(paymentPlans, (x) => x.currencyCost?.price ?? 0);

    const isFree = creditCostSum === 0 && currencyCostSum === 0;
    const hasMembershipBenefit = usages.length > 0;

    const ticketQuantity = overrideQuantity ?? paymentPlans.length;
    const { ticketName } = firstPlan;
    const creditCost = creditCostSum / 100;
    const currencyCost = currencyCostSum / 100;

    const allRates = paymentPlans.flatMap((x) => x.currencyCost?.rates ?? []);
    const originalCost = sumBy(allRates, (x) => x.originalPrice) / 100;
    const isDiscounted = currencyCost < originalCost;

    return [
        {
            ticketName: getTicketQuantityText(ticketQuantity, ticketName),
            priceText: getPriceText(isFree, creditCost, currencyCost, locale, t),
            priceSecondaryLabel: isFree && hasMembershipBenefit ? t.inclInMembership : undefined,
            isFree,
            ticketDetail: isDiscounted
                ? `${getLocaleNumberFormatNoDecimals(locale, originalCost)}`
                : undefined,
            ticketDetailStrikeThrough: isDiscounted,
            isLoadingPrice: isLoadingPrices,
        },
    ];
}

function mapQuantityToSummaryPriceLineForNonMembership(
    quantity: TicketOptionWithQuantity,
    appliedPromoCode: BilberryPromoCodeStatus | null,
    promoCodeUsageContext: PromoCodeUsageContext,
    isLoadingPrices: boolean,
    locale: string,
    currency: Currency,
    t: Translations,
    ticketType?: TicketType,
): SummaryPriceLineData[] {
    const { totalPriceExDiscounts } = getPriceFromTicketOption(
        quantity,
        ticketType,
        appliedPromoCode,
        promoCodeUsageContext,
    );

    const isWaitingListItem = isWaitingListTicketOption(quantity);
    const depositMessage = isWaitingListItem
        ? undefined
        : getDepositMessageForQuantity(quantity, t, locale);

    const baseTicketName = getTicketNameForQuantity(quantity);
    const ticketName = isWaitingListItem ? `${baseTicketName} - ${t.waiting_list}` : baseTicketName;

    const isFree = totalPriceExDiscounts === 0 && !isWaitingListItem;
    const priceText = isFree ? t.free : getPriceTextForQuantity(locale, totalPriceExDiscounts);
    const ticketDetail = getTicketDetailForQuantity(quantity, locale, currency, t);

    return [
        {
            ticketName: ticketName,
            ticketDetail: ticketDetail,
            ticketDetailStrikeThrough: false,
            priceText: priceText,
            priceTextStrikeThrough: isWaitingListItem,
            isFree,
            depositMessage: depositMessage,
            isLoadingPrice: isLoadingPrices,
        },
    ];
}

export function mapQuantityToSummaryPriceLine(
    quantity: TicketOptionWithQuantity,
    appliedPromoCode: BilberryPromoCodeStatus | null,
    promoCodeUsageContext: PromoCodeUsageContext,
    isLoadingPrices: boolean,
    locale: string,
    currency: Currency,
    t: Translations,
    isDetailed: boolean,
    ticketType?: TicketType,
): SummaryPriceLineData[] {
    if (quantity.membership) {
        const productType = getProductTypeForQuantity(quantity);
        const isAccommodation = productType === 'accommodation';
        const isDaysOrNights = productType === 'nights' || productType === 'days';

        const overrideQuantity = isDaysOrNights
            ? getDurationCountForQuantity(quantity, true)
            : undefined; // Special case where we don't want to count the tickets, but use ticket option's quantity as it reflects number of days/nights

        return isDetailed
            ? mapQuantityToDetailedSummaryPriceLineForMembership(
                  quantity.membership,
                  isAccommodation,
                  isLoadingPrices,
                  locale,
                  t,
                  overrideQuantity,
              )
            : mapQuantityToSummaryPriceLineForMembership(
                  quantity.membership,
                  isAccommodation,
                  isLoadingPrices,
                  locale,
                  t,
                  overrideQuantity,
              );
    }

    return mapQuantityToSummaryPriceLineForNonMembership(
        quantity,
        appliedPromoCode,
        promoCodeUsageContext,
        isLoadingPrices,
        locale,
        currency,
        t,
        ticketType,
    );
}

// Render one covered line the legacy-membership way: money-to-pay (or "Gratis") with the original
// struck through, plus punches as a secondary "N klipp" label. Native-specific vs legacy: a punch can
// give a *partial* discount, so a line pays a punch AND money — punches live in the secondary label
// while priceText holds the remaining money.
export function buildNativeSummaryLineForCategory(
    category: {
        name: string;
        quantity: number;
        unitPrice: number;
        discount: number;
        punches: number;
    },
    locale: string,
    t: Translations,
    isLoadingPrices: boolean,
): SummaryPriceLineData {
    const gross = category.unitPrice * category.quantity;
    const moneyToPay = Math.max(0, gross - category.discount);
    const isFree = moneyToPay === 0;
    // Strike the original only when money is still paid (a partial discount). A fully-covered line
    // shows "Gratis" with no strikethrough, matching the legacy look.
    const isDiscounted = category.discount > 0 && moneyToPay > 0;
    const punchLabel =
        category.punches > 0 ? `${category.punches} ${t.credit.num(category.punches)}` : undefined;

    return {
        ticketName: getTicketQuantityText(category.quantity, category.name),
        priceText: isFree ? t.free : getLocaleNumberFormatNoDecimals(locale, moneyToPay),
        ticketDetail: isDiscounted ? getLocaleNumberFormatNoDecimals(locale, gross) : undefined,
        ticketDetailStrikeThrough: isDiscounted,
        priceSecondaryLabel: punchLabel ?? (isFree ? t.inclInMembership : undefined),
        isFree,
        isLoadingPrice: isLoadingPrices,
    };
}

// Native membership per-line summary: build the discounted / "Gratis" / "N klipp" rows from the
// calculate result, mirroring the legacy membership look. Coverage is aggregated by ticket category
// across the item's tours (a multi-day item expands to several) and any manual sub-groups, keyed on
// the same line_key the reserve uses. Returns null when the item has no eligible coverage, so the
// caller falls back to the normal gross mapping (e.g. accommodation, packages, ineligible members).
export function getNativeSummaryPriceLines(
    item: CartItem,
    nativeResult: NativeCalculateResult,
    checkoutInfo: CheckoutInfo,
    locale: string,
    t: Translations,
    isLoadingPrices: boolean,
): SummaryPriceLineData[] | null {
    const memberCard = nativeResult.member_card;
    if (!memberCard) return null;

    const lineItemByPriceId = new Map(
        (nativeResult.line_items ?? []).map((li) => [String(li.plan_price_id), li]),
    );
    // The calculate result's price_category can be an untranslated code, so resolve each plan_price_id
    // to the cart's localized ticket name via the product instance's ticket options.
    const nameByPriceId = new Map<string, string>();
    for (const product of item.products) {
        for (const to of product.ticketOptions) {
            const cartTicketOption = item.ticketOptions.find(
                (cto) => cto.ticketCategoryId === to.ticketCategoryId,
            );
            if (cartTicketOption) nameByPriceId.set(String(to.id), cartTicketOption.name);
        }
    }

    type CategoryAgg = {
        name: string;
        quantity: number;
        unitPrice: number;
        discount: number;
        punches: number;
    };
    const byCategory = new Map<string, CategoryAgg>();

    const accumulate = (cartLineKey: string, planPriceId: string, quantity: number) => {
        if (quantity <= 0) return;
        const lineItem = lineItemByPriceId.get(planPriceId);
        if (!lineItem) return;

        // Sum coverage across the auto line and any manual sub-groups (`${cartLineKey}:${id}:gN`).
        const base = `${cartLineKey}:${planPriceId}`;
        let discount = 0;
        let punches = 0;
        for (const line of memberCard.per_line) {
            if (line.line_key === base || line.line_key.startsWith(`${base}:g`)) {
                discount += line.discount ?? 0;
                punches += line.punches ?? 0;
            }
        }

        const agg = byCategory.get(planPriceId) ?? {
            name: nameByPriceId.get(planPriceId) ?? lineItem.price_category,
            quantity: 0,
            unitPrice: lineItem.unit_price,
            discount: 0,
            punches: 0,
        };
        agg.quantity += quantity;
        agg.discount += discount;
        agg.punches += punches;
        byCategory.set(planPriceId, agg);
    };

    for (const tour of getToursFromCart([item], checkoutInfo)) {
        const cartLineKey = nativeTourLineKey(tour);
        for (const pax of tour.pax) accumulate(cartLineKey, String(pax.id), pax.quantity);
    }
    for (const project of getProjectsFromCart([item])) {
        const cartLineKey = nativeProjectLineKey(project);
        for (const ticket of project.tickets) {
            accumulate(cartLineKey, String(ticket.id), ticket.quantity);
        }
    }

    const aggregated = [...byCategory.values()];
    // No coverage on any category → let the caller render the normal gross lines.
    if (!aggregated.some((a) => a.discount > 0 || a.punches > 0)) return null;

    return aggregated.map((a) => buildNativeSummaryLineForCategory(a, locale, t, isLoadingPrices));
}

export function cartItemToSummaryCartItem(
    item: CartItem,
    isLoadingPrices: boolean,
    isUsingPaymentPlan: boolean,
    appliedPromoCode: BilberryPromoCodeStatus | null,
    locale: string,
    currency: Currency,
    t: Translations,
    nativeResult?: NativeCalculateResult | null,
    checkoutInfo?: CheckoutInfo | null,
): SummaryCartItem {
    const isAccommodation = item.products[0]?.product?.type === 'accommodation';

    // For accommodation, Visbook returns a single total per booking (already
    // encoding the guest count). Each ticket option that has quantity > 0 would
    // otherwise produce its own summary line at the full total — duplicating
    // the price. Collapse to one ticket option so we render a single line; the
    // ticketName is overridden below to surface the Voksne / Barn breakdown.
    const ticketOptions = isAccommodation
        ? [item.ticketOptions.find((to) => to.quantity > 0) ?? item.ticketOptions[0]]
        : item.ticketOptions;

    const isDetailedLines = true;
    // Native membership: show per-line member-card coverage (discounted / "Gratis" / "N klipp"); falls
    // back to the gross mapping for guest/legacy, accommodation, packages, and ineligible members.
    const nativeLines =
        nativeResult && checkoutInfo
            ? getNativeSummaryPriceLines(
                  item,
                  nativeResult,
                  checkoutInfo,
                  locale,
                  t,
                  isLoadingPrices,
              )
            : null;
    const lines =
        nativeLines ??
        mapQuantitiesToSummaryPriceLines(
            ticketOptions,
            appliedPromoCode,
            isLoadingPrices,
            locale,
            currency,
            t,
            isDetailedLines,
            item.ticketType,
        );

    const enrichedLines = isAccommodation
        ? mergeAccommodationSummaryLines(lines, item.ticketOptions, t)
        : lines;

    const selectedTicketOptionsCount = ticketOptions.filter((q) => q.quantity > 0).length;
    const showTime = item.products.length > 1 || item.products[0]?.showTime;

    const displayTitle = getCartItemDisplayTitle(item);

    const supportsEdit = !item.pkg && !isUsingPaymentPlan;

    return {
        priceLines: enrichedLines,
        isLoadingPrices: isLoadingPrices,
        // Accommodation always renders one merged line; activities scale with
        // the number of selected ticket options.
        expectedPriceLinesCount: isAccommodation ? 1 : Math.max(1, selectedTicketOptionsCount),
        displayTitle: displayTitle,
        dateInformation: showTime ? getCartItemSummaryDateInformation(item) : undefined,
        uniqueKey: getCartItemId(item),
        supportsEdit: supportsEdit,
    } as SummaryCartItem;
}

export function mapQuantitiesToSummaryPriceLines(
    quantities: TicketOptionWithQuantity[],
    appliedPromoCode: BilberryPromoCodeStatus | null,
    isLoadingPrices: boolean,
    locale: string,
    currency: Currency,
    t: Translations,
    isDetailed: boolean,
    ticketType?: TicketType,
) {
    const quantitiesWithQuantity = quantities.filter((quantity) => quantity.quantity > 0);

    const promoCodeUsageContext: PromoCodeUsageContext = {
        promoCodeUsages: new Map<string, number>(),
        promoCodeUsagesPerOrder: new Map<string, number>(),
    };

    return quantitiesWithQuantity.flatMap((x) => {
        const mapped = mapQuantityToSummaryPriceLine(
            x,
            appliedPromoCode,
            promoCodeUsageContext,
            isLoadingPrices,
            locale,
            currency,
            t,
            isDetailed,
            ticketType,
        );
        return mapped;
    });
}

function getTicketDetailForQuantity(
    quantity: TicketOptionWithQuantity,
    locale: string,
    currency: Currency,
    t: Translations,
) {
    const productType = getProductTypeForQuantity(quantity);
    const isTimeslot = productType === 'timeslot';
    const isTimepoint = productType === 'timepoint';
    const isNights = productType === 'nights';
    const isDays = productType === 'days';
    const isAccommodation = productType === 'accommodation';
    const isPackage = !!(quantity as PackageTicketOptionWithQuantity).products;

    const countNights = isNights || isAccommodation;
    const durationCount = getDurationCountForQuantity(quantity, countNights);

    if (isTimeslot) return '';

    if (isTimepoint || isPackage) {
        // The parenthetical shows the per-unit price; for a single ticket that just repeats the
        // line total on the right, so omit it (mirrors the native-coverage line, which shows no
        // parenthetical on an uncovered line).
        return quantity.quantity === 1
            ? undefined
            : `${formatCurrency(locale, currency, quantity.price)}`;
    }

    if (countNights) return `${durationCount} ${t.night.num(durationCount)}`;

    if (isDays) return `${durationCount} ${t.days.num(durationCount)}`;
}

function getDurationCountForQuantity(quantity: TicketOptionWithQuantity, countNights: boolean) {
    if (countNights) {
        return getDuration(quantity.productInstances, 'nights');
    }

    return getDuration(quantity.productInstances);
}

function getPriceTextForQuantity(locale: string, totalPriceExDiscounts: number) {
    return getLocaleNumberFormatNoDecimals(locale, totalPriceExDiscounts);
}

function getProductTypeForQuantity(quantity: TicketOptionWithQuantity) {
    return quantity.productInstances[0]?.product?.type ?? '';
}

function getTicketNameForQuantity(quantity: TicketOptionWithQuantity) {
    return getTicketQuantityText(quantity.quantity, quantity.name);
}

function getTicketQuantityText(quantity: number, ticketName: string) {
    return `${quantity}x ${ticketName}`;
}

function getDepositMessageForQuantity(
    quantity: TicketOptionWithQuantity,
    t: Translations,
    locale: string,
) {
    const depositAmount = quantity.depositAmount ? quantity.depositAmount * quantity.quantity : 0;
    return depositAmount
        ? t.only_deposit_amount_to_pay_now.parsed(
              getLocaleNumberFormatNoDecimals(locale, depositAmount),
          )
        : '';
}

function getDuration(productInstances: ProductInstance[], type: 'nights' | 'days' = 'days') {
    const groupedByDate = groupBy(productInstances, (instance) =>
        instance.start.format('YYYY-MM-DD'),
    );

    const lastProductInstanceIsCopied =
        productInstances.length > 1 &&
        productInstances[productInstances.length - 2].id ===
            productInstances[productInstances.length - 1].id;

    const distinctDatesCount = Object.keys(groupedByDate).length;

    // there's a "hack" where we copy a product instance for accommodations to be able to select a time range that ends on a day that is closed
    // for type "nights": under normal circumstances, the duration will be 1 less of the number of distinct dates
    // But in case where the last product instance is a copy, we should not deduct 1 from the count of distinct dates
    const deduction = type === 'nights' && !lastProductInstanceIsCopied ? 1 : 0;

    return distinctDatesCount - deduction;
}

export function getSummaryCartItemsByTypeFromCartItems(
    cartItems: CartItem[],
    appliedPromoCode: BilberryPromoCodeStatus | null,
    locale: string,
    currency: Currency,
    t: Translations,
    isLoadingPrices: boolean,
    isUsingPaymentPlan: boolean,
    nativeResult?: NativeCalculateResult | null,
    checkoutInfo?: CheckoutInfo | null,
) {
    const extraCartItems = cartItems.filter(isExtraCartItem);

    // Filter out the extra cart items, to avoid displaying them twice
    // TODO: maybe this can be done in a more elegant way. But need more insights on the data structure first
    const remainingCartItems = cartItems.filter((item) => !extraCartItems.includes(item));
    const activityCartItems = remainingCartItems.filter(isActivityCartItem);
    const accommodationCartItems = remainingCartItems.filter(isAccomodationOrNightsCartItem);
    const packageCartItems = remainingCartItems.filter(isPackageCartItem);
    const timeslotCartItems = remainingCartItems.filter(isTimeslotCartItem);

    const mapToSummaryCartItems = (items: CartItem[]) =>
        items.map((item) =>
            cartItemToSummaryCartItem(
                item,
                isLoadingPrices,
                isUsingPaymentPlan,
                appliedPromoCode,
                locale,
                currency,
                t,
                nativeResult,
                checkoutInfo,
            ),
        );

    return {
        [t.accommodations]: mapToSummaryCartItems(accommodationCartItems),
        [t.activities]: mapToSummaryCartItems(activityCartItems),
        [t.packages]: mapToSummaryCartItems(packageCartItems),
        [t.entrance]: mapToSummaryCartItems(timeslotCartItems),
        [t.extras]: mapToSummaryCartItems(extraCartItems),
    } as Record<string, SummaryCartItem[]>;
}

export function getSummaryCartItemsByTypeFromOctoOrder(
    isLoadingOctoOrder: boolean,
    octoOrder: OctoOrder | undefined,
    bookings: OctoBooking[],
    locale: string,
    t: Translations,
) {
    if (isLoadingOctoOrder || !octoOrder) return {}; // TODO: what to show when loading

    const summaryCartItems = bookings.map((booking) => {
        const priceLines = getGroupedPriceLinesForOctoBooking(booking, locale, t);

        const start = tzdate(booking.availability.localDateTimeStart);
        const end = tzdate(booking.availability.localDateTimeEnd);

        return {
            displayTitle: booking.product.title,
            uniqueKey: booking.id,
            isLoadingPrices: false,
            dateInformation: start.format('lll') + ' - ' + end.format('lll'),
            priceLines: priceLines,
            supportsEdit: false,
        } as SummaryCartItem;
    });

    return {
        [t.activities]: summaryCartItems,
    } as Record<string, SummaryCartItem[]>;
}

function getGroupedPriceLinesForOctoBooking(
    booking: OctoBooking,
    locale: string,
    t: Translations,
): SummaryPriceLineData[] {
    const groupedUnitItems = groupBy(
        booking.unitItems,
        (unitItem) => `${unitItem.unit.id}-${unitItem.pricing.retail}`,
    );

    return Object.values(groupedUnitItems).map((group) => {
        const [firstItem] = group;
        const isFree = firstItem.pricing.retail === 0;
        const totalPrice = (group.length * firstItem.pricing.retail) / 100;
        return {
            ticketName: getTicketQuantityText(group.length, firstItem.unit.title),
            ticketDetailStrikeThrough: false,
            priceText: getPriceText(isFree, 0, totalPrice, locale, t),
            isFree,
        } as SummaryPriceLineData;
    });
}

function createSummaryPriceLinePlaceholder(isLoadingPrice: boolean): SummaryPriceLineData {
    return {
        ticketName: '',
        priceText: '',
        ticketDetailStrikeThrough: false,
        isLoadingPrice,
    };
}
